import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Info, Eye, EyeOff, Trophy, Users, Calculator, AlertTriangle } from "lucide-react";

// Import existing types
import { FinalizedScore } from "../types/judge";

// Helper function to format scores without unnecessary decimals
const formatScore = (score: number): string => {
  // If the score is a whole number, return it without decimals
  if (score % 1 === 0) {
    return score.toString();
  }
  // Otherwise, return with one decimal place
  return score.toFixed(1);
};

// Type definitions for FlexibleScoreDisplay
interface JudgeScore {
  judgeId?: string;
  totalScore: number;
}

interface DroppedScore {
  totalScore: number;
  judgeId?: string;
}

interface ScoreCalculation {
  [key: string]: any; // Flexible calculation data structure
}

// Use FinalizedScore as the base type with additional optional properties
interface FlexibleScoreData {
  // Required properties from FinalizedScore
  routineId: string;
  routineIndex: number;
  averageScore: number;
  judgeCount: number;
  submittedTime: string;
  
  // Optional properties
  finalScore?: number;
  judges?: { [judgeId: string]: number };
  droppedScores?: number[] | DroppedScore[];
  
  // Additional properties for flexible scoring
  preliminaryScore?: number;
  judgeScores?: JudgeScore[];
  calculation?: ScoreCalculation;
  isTieBroken?: boolean;
  appliedRules?: string[];
}

interface RoutineData {
  routineNumber?: string;
  name?: string;
  routineName?: string;
  "Routine Name"?: string;
  performerName?: string;
  "Performer Name"?: string;
  studioCode?: string;
  "Studio Code"?: string;
}

interface FlexibleScoreDisplayProps {
  scoreData?: FinalizedScore | null;
  routine?: RoutineData | null;
  className?: string;
  showDetails?: boolean;
  onToggleDetails?: () => void;
}

interface CompactFlexibleScoreDisplayProps {
  scoreData?: FinalizedScore | null;
  className?: string;
}

/**
 * Component to display flexible scoring results with detailed breakdown
 */
export function FlexibleScoreDisplay({ 
  scoreData, 
  routine, 
  className = "",
  showDetails = false,
  onToggleDetails 
}: FlexibleScoreDisplayProps): React.ReactElement {
  const [showBreakdown, setShowBreakdown] = useState<boolean>(false);

  if (!scoreData) {
    return (
      <div className={`text-center text-gray-500 ${className}`}>
        No scoring data available
      </div>
    );
  }

  // Type guard to check if scoreData has additional flexible scoring properties
  const hasFlexibleProperties = (data: FinalizedScore): data is FinalizedScore & FlexibleScoreData => {
    return 'isTieBroken' in data || 'appliedRules' in data || 'preliminaryScore' in data;
  };

  // Handle both legacy and flexible scoring data
  const isFlexibleScore = scoreData.finalScore !== undefined;
  const displayScore = isFlexibleScore ? scoreData.finalScore! : scoreData.averageScore;
  const judgeCount = scoreData.judgeCount || 0;
  
  // Handle droppedScores compatibility (could be number[] or DroppedScore[])
  const droppedScores = (scoreData.droppedScores || []).map(score => 
    typeof score === 'number' ? { totalScore: score } : score
  );
  
  // Safely access flexible scoring properties
  const flexibleData = hasFlexibleProperties(scoreData) ? scoreData : null;
  const isTieBroken = flexibleData?.isTieBroken || false;
  const appliedRules = flexibleData?.appliedRules || [];

  const getScoreColor = (score: number): string => {
    if (score >= 270) return "text-purple-400"; // 90%+ of 300
    if (score >= 240) return "text-blue-400";   // 80%+ of 300
    if (score >= 210) return "text-green-400";  // 70%+ of 300
    if (score >= 180) return "text-yellow-400"; // 60%+ of 300
    return "text-red-400";
  };

  const getScoreBgColor = (score: number): string => {
    if (score >= 270) return "bg-gradient-to-r from-purple-500/30 to-pink-500/30 border-purple-400/50";
    if (score >= 240) return "bg-gradient-to-r from-blue-500/30 to-indigo-500/30 border-blue-400/50";
    if (score >= 210) return "bg-gradient-to-r from-green-500/30 to-emerald-500/30 border-green-400/50";
    if (score >= 180) return "bg-gradient-to-r from-yellow-500/30 to-orange-500/30 border-yellow-400/50";
    return "bg-gradient-to-r from-red-500/30 to-pink-500/30 border-red-400/50";
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Main Score Display */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <motion.div
            className={`inline-block px-6 py-3 rounded-full font-bold backdrop-blur-xl border ${getScoreBgColor(displayScore)}`}
            whileHover={{ scale: 1.05 }}
          >
            <span className="text-2xl">{formatScore(displayScore)}</span>
          </motion.div>
          
          <div className="flex items-center space-x-2">
            <Users className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-300">{judgeCount} judges</span>
            
            {droppedScores.length > 0 && (
              <>
                <span className="text-gray-500">•</span>
                <span className="text-sm text-orange-300">{droppedScores.length} dropped</span>
              </>
            )}
            
            {isTieBroken && (
              <>
                <span className="text-gray-500">•</span>
                <div className="flex items-center space-x-1">
                  <AlertTriangle className="w-3 h-3 text-yellow-400" />
                  <span className="text-sm text-yellow-300">Tie broken</span>
                </div>
              </>
            )}
          </div>
        </div>

        {isFlexibleScore && (
          <button
            onClick={() => setShowBreakdown(!showBreakdown)}
            className="flex items-center space-x-2 px-3 py-1 rounded-lg bg-gray-700/50 hover:bg-gray-600/50 transition-colors"
          >
            {showBreakdown ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span className="text-sm">Details</span>
          </button>
        )}
      </div>

      {/* Detailed Breakdown */}
      <AnimatePresence>
        {showBreakdown && isFlexibleScore && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-gray-800/30 rounded-lg p-4 border border-gray-700/50"
          >
            <div className="space-y-4">
              {/* Score Calculation Steps */}
              <div>
                <h4 className="text-sm font-semibold text-gray-300 mb-2 flex items-center">
                  <Calculator className="w-4 h-4 mr-2" />
                  Score Calculation
                </h4>
                <div className="space-y-2 text-sm">
                  {flexibleData?.preliminaryScore && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Preliminary Score:</span>
                      <span className="text-white">{formatScore(flexibleData.preliminaryScore)}</span>
                    </div>
                  )}
                  
                  {droppedScores.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Dropped Scores:</span>
                      <span className="text-orange-300">
                        {droppedScores.map(score => formatScore(score.totalScore)).join(', ')}
                      </span>
                    </div>
                  )}
                  
                  {isTieBroken && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Tie-Breaking Applied:</span>
                      <span className="text-yellow-300">Yes</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between font-semibold border-t border-gray-600 pt-2">
                    <span className="text-gray-300">Final Score:</span>
                    <span className={getScoreColor(displayScore)}>{formatScore(displayScore)}</span>
                  </div>
                </div>
              </div>

              {/* Judge Scores Breakdown */}
              {flexibleData?.judgeScores && flexibleData.judgeScores.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-300 mb-2">Judge Scores Used</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {flexibleData.judgeScores.map((judge: JudgeScore, index: number) => (
                      <div key={judge.judgeId || index} className="flex justify-between text-sm">
                        <span className="text-gray-400">
                          {judge.judgeId || `Judge ${index + 1}`}:
                        </span>
                        <span className="text-white">{formatScore(judge.totalScore)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Applied Rules */}
              {appliedRules.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-300 mb-2">Applied Rules</h4>
                  <div className="space-y-1">
                    {appliedRules.map((ruleId: string, index: number) => (
                      <div key={index} className="text-sm text-blue-300 flex items-center">
                        <Info className="w-3 h-3 mr-2" />
                        {ruleId.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Calculation Details */}
              {flexibleData?.calculation && (
                <div className="text-xs text-gray-500 border-t border-gray-600 pt-2">
                  <details>
                    <summary className="cursor-pointer hover:text-gray-400">
                      View Calculation Steps
                    </summary>
                    <pre className="mt-2 p-2 bg-gray-900/50 rounded text-xs overflow-auto">
                      {JSON.stringify(flexibleData.calculation, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Legacy Score Info */}
      {!isFlexibleScore && (
        <div className="text-sm text-gray-400">
          <span>Average score from {judgeCount} judges</span>
        </div>
      )}
    </div>
  );
}

/**
 * Compact version for table display
 */
export function CompactFlexibleScoreDisplay({ 
  scoreData, 
  className = "" 
}: CompactFlexibleScoreDisplayProps): React.ReactElement {
  if (!scoreData) return <span className="text-gray-500">-</span>;

  // Type guard to check if scoreData has additional flexible scoring properties
  const hasFlexibleProperties = (data: FinalizedScore): data is FinalizedScore & FlexibleScoreData => {
    return 'isTieBroken' in data || 'appliedRules' in data || 'preliminaryScore' in data;
  };

  const isFlexibleScore = scoreData.finalScore !== undefined;
  const displayScore = isFlexibleScore ? scoreData.finalScore! : scoreData.averageScore;
  
  // Handle droppedScores compatibility (could be number[] or DroppedScore[])
  const droppedScores = (scoreData.droppedScores || []).map(score => 
    typeof score === 'number' ? { totalScore: score } : score
  );
  
  // Safely access flexible scoring properties
  const flexibleData = hasFlexibleProperties(scoreData) ? scoreData : null;
  const isTieBroken = flexibleData?.isTieBroken || false;

  const getScoreColor = (score: number): string => {
    return "text-blue-200";
  };

  const getScoreBgColor = (score: number): string => {
    return "bg-gradient-to-r from-blue-500/30 to-indigo-500/30 border-blue-400/50";
  };

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <span
        className={`inline-block px-4 py-2 rounded-full font-bold backdrop-blur-xl border ${getScoreBgColor(displayScore)}`}
      >
        {formatScore(displayScore)}
      </span>
      
      {isFlexibleScore && (droppedScores.length > 0 || isTieBroken) && (
        <div className="flex items-center space-x-1">
          {droppedScores.length > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-300 border border-orange-400/30">
              -{droppedScores.length}
            </span>
          )}
          {isTieBroken && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300 border border-yellow-400/30">
              TB
            </span>
          )}
        </div>
      )}
    </div>
  );
}
